import type { CityConfig } from "../city-template/types";

export const bowlingGreenConfig: CityConfig = {
  city: "Bowling Green",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "bowling-green",
  route: "/cities/bowling-green",
  title: "Emergency Passport Bowling Green, KY | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Bowling Green, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Warren County and South Central Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Bowling Green, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Bowling Green and South Central Kentucky travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bowling%20Green%20Kentucky%20downtown%20cityscape%20at%20golden%20hour%20with%20historic%20Fountain%20Square%20and%20modern%20Western%20Kentucky%20University%20campus%20buildings%2C%20warm%20South%20Central%20Kentucky%20sunset%20light%2C%20charming%20small%20city%20atmosphere%20with%20tree-lined%20streets%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Warren%20County%20scene%20with%20rolling%20green%20hills%20in%20the%20distance&width=1800&height=700&seq=bowlinggreen-hero-001&orientation=landscape",
  heroImageAlt: "Bowling Green Kentucky downtown at golden hour with Fountain Square and Western Kentucky University campus",
  geoPosition: "36.9903;-86.4436",
  geoPlacename: "Bowling Green, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 320 miles from Bowling Green via I-65",
  },
  nearbyCitiesZips: [
    { name: "Bowling Green", zips: "42101, 42102, 42103, 42104" },
    { name: "Glasgow", zips: "42141, 42142" },
    { name: "Franklin", zips: "42134, 42135" },
    { name: "Scottsville", zips: "42164" },
    { name: "Auburn", zips: "42206" },
    { name: "Hopkinsville", zips: "42240, 42241" },
    { name: "Russellville", zips: "42276" },
    { name: "Lebanon", zips: "40033" },
    { name: "Nashville (TN)", zips: "37011–37250" },
    { name: "Smiths Grove", zips: "42171" },
    { name: "Morgantown", zips: "42261" },
    { name: "Woodburn", zips: "42170" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Warren County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/cities/louisville" },
    { name: "Fast passport renewal in Lexington", route: "/cities/lexington" },
    { name: "Emergency passport help in Nashville", route: "/nashville" },
    { name: "Urgent passport services in Glasgow", route: "/cities/glasgow" },
    { name: "Same-day passport help in Hopkinsville", route: "/cities/hopkinsville" },
    { name: "Passport courier services near Franklin", route: "/franklin" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Bowling Green remotely via secure courier. We do not have a walk-in office in Bowling Green — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Warren County travelers.",
  fpcOfficeDistance: "Serving all of Warren County and South Central Kentucky",
  serviceAreaIntro: "From downtown Bowling Green to the Western Kentucky University campus, from the historic district to the surrounding South Central Kentucky region — Fast Passport Center serves the entire Bowling Green community. We regularly help travelers from Glasgow, Franklin, and Scottsville get their passports fast.",
  nearbyCitiesIntro: "We serve all Bowling Green neighborhoods and nearby South Central Kentucky cities including Glasgow, Franklin, Scottsville, Auburn, Hopkinsville, Russellville, Lebanon, Nashville (TN), Smiths Grove, Morgantown, and Woodburn. If you are in Warren County, south-central Kentucky, or the Nashville metro area, we have you covered.",
  howItWorksIntro: "Most Bowling Green travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Bowling Green travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Bowling Green and Warren County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Bowling Green? Here are the most common ones we hear from Warren County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Warren County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jessica W.",
      location: "Bowling Green, KY",
      rating: 5,
      text: "My passport expired the week before my study abroad semester in Japan. Fast Passport Center got me a renewal in 3 days. I made my flight to Tokyo and didn't miss orientation.",
      service: "Passport Renewal",
    },
    {
      name: "Brian T.",
      location: "Glasgow, KY",
      rating: 5,
      text: "Emergency business trip to Toronto came up last minute. They got me a passport in 36 hours. Professional, reliable, and kept me updated every step of the way.",
      service: "Emergency Passport",
    },
    {
      name: "Amanda S.",
      location: "Franklin, KY",
      rating: 5,
      text: "First-time passport for my family's Disney cruise to the Bahamas. They helped me find the nearest acceptance facility and I had my passport in 5 days. Great customer service.",
      service: "New Passport",
    },
    {
      name: "Kevin M.",
      location: "Hopkinsville, KY",
      rating: 5,
      text: "Lost my passport before my military deployment leave to visit family in Germany. They got me a replacement in 24 hours. I can't thank them enough for their speed and professionalism.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "bowling-green-1",
      name: "Bowling Green Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "805 Campbell Ln",
      city: "Bowling Green",
      state: "KY",
      zip: "42104",
      phone: "(270) 843-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Bowling Green",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Warren County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "bowling-green-2",
      name: "Glasgow Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 S Public Sq",
      city: "Glasgow",
      state: "KY",
      zip: "42141",
      phone: "(270) 651-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Glasgow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Barren County and eastern Warren County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "bowling-green-3",
      name: "Hopkinsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "110 S Main St",
      city: "Hopkinsville",
      state: "KY",
      zip: "42240",
      phone: "(270) 885-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Hopkinsville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Christian County and Fort Campbell travelers. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "bowling-green-4",
      name: "Franklin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "500 S Main St",
      city: "Franklin",
      state: "KY",
      zip: "42134",
      phone: "(270) 586-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Franklin",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Simpson County and western Warren County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "bowling-green-5",
      name: "Nashville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "525 Royal Pkwy",
      city: "Nashville",
      state: "TN",
      zip: "37229",
      phone: "(615) 872-5511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Nashville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Nashville metro area. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "bowling-green-6",
      name: "Warren County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "429 E 10th Ave",
      city: "Bowling Green",
      state: "KY",
      zip: "42101",
      phone: "(270) 842-9416",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Warren County Courthouse",
      website: "https://www.warrencountyky.gov/",
      websiteLabel: "Warren County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Warren County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Warren County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Bowling Green applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Warren County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Warren County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Bowling Green." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Bowling Green address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Bowling Green before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Bowling Green for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Bowling Green?", a: "Fast Passport Center offers fast passport renewal in Bowling Green in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Bowling Green in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Bowling Green?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — a 5 hour drive from Bowling Green — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Warren County residents." },
    { q: "Can I expedite a child passport in Bowling Green?", a: "Yes. Fast Passport Center expedites child passports in Bowling Green for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Warren County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Bowling Green with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Bowling Green?", a: "With Fast Passport Center's emergency courier service, Bowling Green travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Bowling Green?", a: "Popular options include the Bowling Green Post Office (42104), Glasgow Post Office (42141), Hopkinsville Post Office (42240), Franklin Post Office (42134), Nashville Main Post Office (TN), and Warren County Clerk (42101). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Bowling Green?", a: "Same-day passports are only available at the Atlanta Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5 hour drive to Atlanta." },
    { q: "What if I live in a smaller Warren County town?", a: "We serve the entire Warren County and surrounding area including Glasgow, Franklin, Scottsville, Auburn, Hopkinsville, Russellville, Lebanon, Nashville (TN), Smiths Grove, Morgantown, and Woodburn. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Bowling Green?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Bowling Green?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Bowling Green and Glasgow offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Bowling Green?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Bowling Green?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Warren County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Bowling Green", address: "2435 Scottsville Rd, Bowling Green, KY 42104", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Bowling Green", address: "1625 Campbell Ln, Bowling Green, KY 42104", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Bowling Green", address: "150 Walton Ave, Bowling Green, KY 42104", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Bowling Green", address: "1105 State St, Bowling Green, KY 42101", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Glasgow", address: "502 S L Rogers Wells Blvd, Glasgow, KY 42141", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Glasgow", address: "200 S L Rogers Wells Blvd, Glasgow, KY 42141", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Hopkinsville", address: "2800 Fort Campbell Blvd, Hopkinsville, KY 42240", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Bowling Green", address: "2435 Scottsville Rd, Bowling Green, KY 42104", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Warren County",
      address: "429 E 10th Ave, Bowling Green, KY 42101",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Barren County",
      address: "117 N Public Sq, Glasgow, KY 42141",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Simpson County",
      address: "101 N Main St, Franklin, KY 42134",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Simpson County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Christian County",
      address: "511 S Main St, Hopkinsville, KY 42240",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Christian County and Fort Campbell residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BWG",
      name: "Bowling Green-Warren County Regional",
      distance: "5 miles from downtown Bowling Green",
      address: "985 Three Springs Rd, Bowling Green, KY 42104",
      note: "The primary airport for Warren County. General aviation and limited commercial service. Domestic flights with connections to major hubs.",
      flightUrl: "https://www.bgwcairport.com/",
      flightLabel: "Bowling Green Airport",
      featured: true,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "65 miles from Bowling Green",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Major international hub for long-haul flights to Europe, Asia, and the Caribbean. Accessible via I-65.",
      flightUrl: "https://www.flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "120 miles from Bowling Green",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport for international flights. Accessible via I-65.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
  ],
};